import * as THREE from "three";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils";

const ANIMATION_MIXER_USER_DATA_KEY = "__artetraAnimationMixers";

function makeAnimationMixerMetadataNonEnumerable(object: THREE.Object3D) {
    object.traverse((node) => {
        const descriptor = Object.getOwnPropertyDescriptor(node.userData, ANIMATION_MIXER_USER_DATA_KEY);
        if (!descriptor?.enumerable) return;

        Object.defineProperty(node.userData, ANIMATION_MIXER_USER_DATA_KEY, {
            ...descriptor,
            enumerable: false,
        });
    });
}

/**
 * Returns width/height/depth and center/min/max for an object.
 */
export function getObjectDimensions(object: THREE.Object3D | THREE.Scene | THREE.Group | THREE.Mesh) {
    const boundingBox = new THREE.Box3().setFromObject(object);
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);

    return {
        width: boundingBox.max.x - boundingBox.min.x,
        height: boundingBox.max.y - boundingBox.min.y,
        depth: boundingBox.max.z - boundingBox.min.z,
        center,
        min: boundingBox.min.clone(),
        max: boundingBox.max.clone()
    };
}

/**
 * Uniformly scales a mesh so its height matches the given value.
 */
export function scaleMeshToMatchHeight(object: any, height: number) {
    const dimensions = getObjectDimensions(object);
    const scaleFactor = height / dimensions.height;

    if (object.scale) {
        const { x, y, z } = object.scale;
        object.scale.set(x * scaleFactor, y * scaleFactor, z * scaleFactor);
    }

    return { ...dimensions, scaleFactor };
}

function hasDeformedMeshes(object3D: THREE.Object3D) {
    let hasDeformedMesh = false;
    object3D.traverse((child: any) => {
        if (hasDeformedMesh || !child.isMesh || !child.geometry) {
            return;
        }

        if (child.isSkinnedMesh || (Array.isArray(child.morphTargetInfluences) && child.morphTargetInfluences.length > 0)) {
            hasDeformedMesh = true;
        }
    });

    return hasDeformedMesh;
}

function computeWorldBoundsFromVisibleVertices(object3D: THREE.Object3D) {
    const bounds = new THREE.Box3();
    const localVertex = new THREE.Vector3();
    const worldVertex = new THREE.Vector3();
    let hasVertices = false;

    object3D.updateMatrixWorld(true);
    object3D.traverse((child: any) => {
        if (!child.isMesh || !child.geometry?.attributes?.position) {
            return;
        }

        const position = child.geometry.attributes.position;
        for (let index = 0; index < position.count; index++) {
            if (typeof child.getVertexPosition === "function") {
                child.getVertexPosition(index, localVertex);
            } else {
                localVertex.fromBufferAttribute(position, index);
            }

            worldVertex.copy(localVertex).applyMatrix4(child.matrixWorld);
            bounds.expandByPoint(worldVertex);
            hasVertices = true;
        }
    });

    return hasVertices ? bounds : null;
}

/**
 * Computes a world-space bounding box for an Object3D by merging all child mesh bounds.
 */
export function computeWorldBounds(object3D: THREE.Object3D) {
    const merged = new THREE.Box3();
    const temp = new THREE.Box3();
    const has = { value: false } as any;
    object3D.updateMatrixWorld(true);
    object3D.traverse((child: any) => {
        if (child.isMesh && child.geometry) {
            if (!child.geometry.boundingBox) {
                child.geometry.computeBoundingBox();
            }
            temp.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
            if (!has.value) {
                merged.copy(temp);
                has.value = true;
            } else {
                merged.union(temp);
            }
        }
    });
    return merged;
}

export function computeVisibleWorldBounds(object3D: THREE.Object3D) {
    if (hasDeformedMeshes(object3D)) {
        return computeWorldBoundsFromVisibleVertices(object3D) ?? computeWorldBounds(object3D);
    }

    return computeWorldBounds(object3D);
}

/**
 * Computes a world-space bounding sphere for an Object3D by merging all child mesh bounds.
 */
export function computeWorldBoundingSphere(object3D: THREE.Object3D) {
    const merged = new THREE.Sphere();
    const temp = new THREE.Sphere();
    const tempBox = new THREE.Box3();
    const has = { value: false } as any;
    object3D.updateMatrixWorld(true);

    object3D.traverse((child: any) => {
        if (child.isMesh && child.geometry) {
            if (!child.geometry.boundingSphere) {
                child.geometry.computeBoundingSphere();
            }

            temp.copy(child.geometry.boundingSphere);
            temp.applyMatrix4(child.matrixWorld);

            if (!has.value) {
                merged.copy(temp);
                has.value = true;
            } else {
                const distance = merged.center.distanceTo(temp.center);
                const combinedRadius = distance + Math.max(merged.radius, temp.radius);

                if (distance + temp.radius <= merged.radius) {
                    // temp inside merged – keep merged
                } else if (distance + merged.radius <= temp.radius) {
                    merged.copy(temp);
                } else {
                    const newRadius = (distance + merged.radius + temp.radius) * 0.5;
                    const centerOffset = (temp.radius - merged.radius + distance) * 0.5;
                    merged.center.lerp(temp.center, centerOffset / distance);
                    merged.radius = newRadius;
                }
            }
        }
    });

    return merged;
}

export function computeVisibleWorldBoundingSphere(object3D: THREE.Object3D) {
    if (hasDeformedMeshes(object3D)) {
        const bounds = computeWorldBoundsFromVisibleVertices(object3D);
        return bounds ? bounds.getBoundingSphere(new THREE.Sphere()) : computeWorldBoundingSphere(object3D);
    }

    return computeWorldBoundingSphere(object3D);
}

export function createHexagon(radius: number, color: string, emissive?: string): THREE.Mesh {
    const hexagonGeometry = new THREE.CylinderGeometry(radius, radius, 0.0001, 6);
    const hexagonMaterial = new THREE.MeshPhysicalMaterial({ color: color, roughness: 1, metalness: 0, side: THREE.DoubleSide });
    if (emissive) {
        hexagonMaterial.emissive = new THREE.Color(emissive);
        hexagonMaterial.emissiveIntensity = 0.01;
    }
    const hexagonMesh = new THREE.Mesh(hexagonGeometry, hexagonMaterial);
    hexagonMesh.rotation.y = Math.PI / 6;
    return hexagonMesh;
}

export const createBoundingCylinder = (object: THREE.Object3D, animations?: THREE.AnimationClip[]) => {
    if (animations && animations.length > 0 && object) {
        makeAnimationMixerMetadataNonEnumerable(object);
        const clone = cloneSkeleton(object);
        const mixer = new THREE.AnimationMixer(clone);
        const actions = animations.map((clip) => mixer.clipAction(clip));
        actions[0].play();

        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

        mixer.setTime(0);
        clone.updateMatrixWorld(true);

        const worldBB = new THREE.Box3().setFromObject(clone);
        if (!worldBB || !isFinite(worldBB.min.x) || !isFinite(worldBB.max.x)) {
            const bbox = new THREE.Box3().setFromObject(object);
            const size = new THREE.Vector3();
            const center = new THREE.Vector3();
            bbox.getSize(size);
            bbox.getCenter(center);
            const radius = Math.max(size.x, size.z) * 0.5;
            const height = size.y;
            const geometry = new THREE.CylinderGeometry(radius, radius, height, 8);
            const material = new THREE.MeshBasicMaterial({
                color: 0xff0000,
                depthTest: false,
                depthWrite: false,
                transparent: true,
                opacity: 0.0
            });
            const cylinder = new THREE.Mesh(geometry, material);
            object.updateMatrixWorld(true);
            object.worldToLocal(center);
            cylinder.position.copy(center);
            cylinder.name = "bounding-cylinder";
            return cylinder;
        }

        const inverseCloneMatrix = new THREE.Matrix4().copy(clone.matrixWorld).invert();
        const corners = [
            new THREE.Vector3(worldBB.min.x, worldBB.min.y, worldBB.min.z),
            new THREE.Vector3(worldBB.min.x, worldBB.min.y, worldBB.max.z),
            new THREE.Vector3(worldBB.min.x, worldBB.max.y, worldBB.min.z),
            new THREE.Vector3(worldBB.min.x, worldBB.max.y, worldBB.max.z),
            new THREE.Vector3(worldBB.max.x, worldBB.min.y, worldBB.min.z),
            new THREE.Vector3(worldBB.max.x, worldBB.min.y, worldBB.max.z),
            new THREE.Vector3(worldBB.max.x, worldBB.max.y, worldBB.min.z),
            new THREE.Vector3(worldBB.max.x, worldBB.max.y, worldBB.max.z),
        ];
        for (let i = 0; i < corners.length; i++) {
            corners[i].applyMatrix4(inverseCloneMatrix);
            if (corners[i].x < minX) minX = corners[i].x;
            if (corners[i].y < minY) minY = corners[i].y;
            if (corners[i].z < minZ) minZ = corners[i].z;
            if (corners[i].x > maxX) maxX = corners[i].x;
            if (corners[i].y > maxY) maxY = corners[i].y;
            if (corners[i].z > maxZ) maxZ = corners[i].z;
        }

        if (!isFinite(minX) || !isFinite(maxX)) {
            const bbox = new THREE.Box3().setFromObject(object);
            const size = new THREE.Vector3();
            const center = new THREE.Vector3();
            bbox.getSize(size);
            bbox.getCenter(center);
            const radius = Math.max(size.x, size.z) * 0.5;
            const height = size.y;
            const geometry = new THREE.CylinderGeometry(radius, radius, height, 8);
            const material = new THREE.MeshBasicMaterial({
                color: 0xff0000,
                depthTest: false,
                depthWrite: false,
                transparent: true,
                opacity: 0.0
            });
            const cylinder = new THREE.Mesh(geometry, material);
            object.updateMatrixWorld(true);
            object.worldToLocal(center);
            cylinder.position.copy(center);
            cylinder.name = "bounding-cylinder";
            return cylinder;
        }

        const sizeX = maxX - minX;
        const sizeY = maxY - minY;
        const sizeZ = maxZ - minZ;

        const centerLocal = new THREE.Vector3(
            minX + sizeX * 0.5,
            minY + sizeY * 0.5,
            minZ + sizeZ * 0.5
        );

        const radius = Math.max(sizeX, sizeZ) * 0.5;
        const height = sizeY;

        const geometry = new THREE.CylinderGeometry(radius, radius, height, 8);
        const material = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.0
        });

        const cylinder = new THREE.Mesh(geometry, material);
        cylinder.position.copy(centerLocal);
        cylinder.name = "bounding-cylinder";
        return cylinder;
    }

    const bbox = new THREE.Box3().setFromObject(object);
    const size = new THREE.Vector3();
    bbox.getSize(size);
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    const radius = Math.max(size.x, size.z) * 0.5;
    const height = size.y;

    const geometry = new THREE.CylinderGeometry(radius, radius, height, 8);
    const material = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.3
    });

    const cylinder = new THREE.Mesh(geometry, material);
    object.updateMatrixWorld(true);
    object.worldToLocal(center);
    cylinder.position.copy(center);
    cylinder.name = "bounding-cylinder";
    return cylinder;
}

